import { useEffect, useState } from 'react';

const FRESHNESS_WINDOW_DAYS = 7;

export type ConnectionState = 'connected' | 'idle' | 'disconnected' | 'unknown';

export interface SourceActivityEntry {
  lastEventAt: string | null;
  state: ConnectionState;
}

export type SourceActivityMap = Record<string, SourceActivityEntry>;

interface ApiResponse {
  sources: Record<string, { lastEventAt: string | null }>;
}

/**
 * Fetches per-source last_event_at from patient_service and derives a
 * three-state connection flag for the BuildTab dot indicator.
 *
 * The freshness threshold lives on the client (7 days). All clients agree
 * on this constant by convention rather than via a server-side knob —
 * tradeoff accepted in favor of removing a network hop and a backend
 * facade. If we need per-env tuning later, move the constant to a config
 * value or move computation back to a server endpoint.
 *
 * Failure mode: if the API call throws (network, auth, gateway), every
 * source reports `unknown`. The UI renders unknown as a neutral gray dot
 * so it never looks like a real "disconnected" signal — that distinction
 * is important for the user (one means "no data flowing"; the other means
 * "we don't know if data is flowing").
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function useSourceActivity(client: Record<string, any> | null, orgId: string) {
  const [activity, setActivity] = useState<SourceActivityMap>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!client?.api?.sourceActivity || !orgId) return;
    let cancelled = false;

    setIsLoading(true);
    client.api.sourceActivity
      .getSourceActivity({ orgId })
      .then((res: ApiResponse) => {
        if (cancelled) return;
        setActivity(deriveStates(res?.sources ?? {}));
      })
      .catch(() => {
        // Degraded mode: surface "unknown" for every known source key so the
        // UI can render neutral dots instead of false "connected" claims.
        if (cancelled) return;
        setActivity(allUnknown());
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [client, orgId]);

  return { activity, isLoading };
}

function deriveStates(sources: Record<string, { lastEventAt: string | null }>): SourceActivityMap {
  const cutoff = Date.now() - FRESHNESS_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const out: SourceActivityMap = {};
  for (const [key, entry] of Object.entries(sources)) {
    const ts = entry?.lastEventAt ? Date.parse(entry.lastEventAt) : NaN;
    let state: ConnectionState;
    if (!entry?.lastEventAt || Number.isNaN(ts)) {
      state = 'disconnected';
    } else if (ts >= cutoff) {
      state = 'connected';
    } else {
      state = 'idle';
    }
    out[key] = { lastEventAt: entry?.lastEventAt ?? null, state };
  }
  return out;
}

function allUnknown(): SourceActivityMap {
  const keys = [
    'NURSE_CALL',
    'PATIENT_MONITOR',
    'HL7_ORM',
    'HL7_ORU',
    'HL7_ADT',
    'HL7_SIU',
    'EMS_CASE',
  ];
  return Object.fromEntries(
    keys.map((k) => [k, { lastEventAt: null, state: 'unknown' as ConnectionState }])
  );
}
